/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Tabs } from '@radix-ui/themes';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import { ContainerListView } from './containers/ContainerListView';
import type { ContainerQuery, QuarantinedContainer } from './containers/containerQuarantine.types';
import { parseContainerQuery, serializeContainerQuery } from './containers/containerQueryUrl';
import { NotAvailableTab } from './NotAvailableTab';
import { QuarantineListView } from './QuarantineListView';
import { QUARANTINE_STRINGS as STRINGS } from './quarantine.strings';

const TABS = ['quarantine', 'cached-risk', 'containers'];

const CONTAINER_DETAIL_STATE = 'preview.security.containerDetail';

/**
 * The tab named by the URL's `tab` opens first, so a page such as the container detail can link back to its
 * tab. Switching tabs does not change the URL.
 *
 * A Containers row opens the container detail, which carries the list's sort and page in `back`; coming back with
 * `tab=containers` and that `back` starts the list on them.
 */
export function QuarantineTabsView(): JSX.Element {
  const { params } = useCurrentStateAndParams();
  const router = useRouter();
  const tab = TABS.includes(String(params.tab)) ? String(params.tab) : 'quarantine';
  const back = typeof params.back === 'string' ? params.back : undefined;
  const containerQuery = tab === 'containers' && back ? parseContainerQuery(back) : undefined;

  const detailParams = (item: QuarantinedContainer, query: ContainerQuery) => ({
    key: item.key,
    back: serializeContainerQuery(query),
  });

  return (
    <Tabs.Root defaultValue={tab}>
      <Tabs.List>
        <Tabs.Trigger value="quarantine" data-analytics-id="nxrm-security-quarantine-tab-quarantine-tab">
          {STRINGS.tabs.quarantine}
        </Tabs.Trigger>
        <Tabs.Trigger value="cached-risk" data-analytics-id="nxrm-security-quarantine-tab-cached-risk-tab">
          {STRINGS.tabs.cachedRisk}
        </Tabs.Trigger>
        <Tabs.Trigger value="containers" data-analytics-id="nxrm-security-quarantine-tab-containers-tab">
          {STRINGS.tabs.containers}
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="quarantine" mt="4">
        <QuarantineListView />
      </Tabs.Content>
      <Tabs.Content value="cached-risk" mt="4">
        <NotAvailableTab />
      </Tabs.Content>
      <Tabs.Content value="containers" mt="4">
        <ContainerListView
          initialQuery={containerQuery}
          onOpen={(item, query) => router.stateService.go(CONTAINER_DETAIL_STATE, detailParams(item, query))}
          detailHref={(item, query) => router.stateService.href(CONTAINER_DETAIL_STATE, detailParams(item, query))}
        />
      </Tabs.Content>
    </Tabs.Root>
  );
}
